import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import TypesService from '../../services/TypesService';

import './DigitsCodeInput.scss';

const codeLength = TypesService.DigitsCodeLength;

const DigitsCodeInput = ({
	className = '',
	value = '',
	onChange = () => {},
	onFill = () => {},
	error = false,
	disabled = false,
	autoFocus = true,
}) => {
	const codeRef = useRef([]);

	const codeArray = value.split('').concat(new Array(codeLength).fill('')).slice(0, codeLength);

	const focusFirstEmply = () => {
		codeRef.current[Math.min(value.length, codeLength - 1)].focus();
	};

	useEffect(() => {
		if (autoFocus) focusFirstEmply();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (autoFocus && !value) focusFirstEmply();
	}, [value]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateValue = (char, index) => {
		if (char && !/^\d+$/.test(char)) return;

		const newValue = [...codeArray.slice(0, index), char, ...codeArray.slice(index + 1)];
		const strValue = newValue.join('');
		onChange(strValue);

		if (char) {
			codeRef.current[Math.min(strValue.length, codeLength - 1)].focus();

			if (strValue.length === codeLength) onFill(strValue);
		}
	};

	const digitKeyDown = (e, index) => {
		const lastIndex = codeLength - 1;
		if (e.key === 'Backspace' && index > 0 && (index < lastIndex || !codeArray[lastIndex])) {
			if (value[index - 1]) {
				codeRef.current[value.length - 1].focus();
			} else {
				codeRef.current[value.length].focus();
			}
		}
	};

	const pasteValue = e => {
		e.preventDefault();
		const pasteValue = e.clipboardData.getData('text').trim();

		if (!pasteValue || !/^\d+$/.test(pasteValue)) return;

		const newCodeValue = (value + pasteValue).slice(0, codeLength);
		onChange(newCodeValue);
		codeRef.current[Math.min(newCodeValue.length, codeLength - 1)].focus();

		if (newCodeValue.length === codeLength) {
			onFill(newCodeValue);
		}
	};

	return (
		<div
			className={classNames('DigitsCodeInput', className, { DigitsCodeInput__error: error })}
			onClick={focusFirstEmply}
		>
			{codeArray.map((digit, index) => (
				<div className='DigitsCodeInput__field-box' key={index}>
					{index > 0 && index % 3 === 0 && <div className='DigitsCodeInput__digits-hyphen'>-</div>}
					<input
						ref={el => (codeRef.current[index] = el)}
						className={classNames('DigitsCodeInput__digit-field FormBlock', {
							'DigitsCodeInput__digit-filled-field': digit,
						})}
						value={digit}
						onChange={e => updateValue(e.target.value, index)}
						maxLength={1}
						disabled={disabled}
						onKeyDown={e => digitKeyDown(e, index)}
						onPaste={pasteValue}
						autoComplete='off'
						autoCorrect='off'
						inputMode='numeric'
					/>
				</div>
			))}
		</div>
	);
};

DigitsCodeInput.propTypes = {
	className: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func,
	onFill: PropTypes.func,
	error: PropTypes.bool,
	disabled: PropTypes.bool,
	autoFocus: PropTypes.bool,
};

export default DigitsCodeInput;
